import { Injectable, inject, signal, effect } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import { Global } from '../../../core/service/core/Global';
import { AuthService } from '@services/core/auth.service';
import { QueryUtils } from 'app/core/utils/query.utils';

export interface ClaimThirdPartyInfo {
  thirdPartyDriverName: string;
  thirdPartyOwnerName: string;
  thirdPartyLicense: string;
  thirdPartyLicenseClass?: string;
  thirdPartyPhone: string;
  plate: string;
  brandCode: number;
  modelCode: number;
  year: number;
  hasInsurance: boolean;
  tookResponsibility: boolean;
}

export interface ClaimDetails {
  id: number;
  uuid: string;
  dateOfLoss: string;
  description: string;
  driverName?: string;
  geographicLocation?: string;
  latitude?: number | null;
  longitude?: number | null;
  amount?: number;       // Monto propuesto original (solo lectura)
  laborCost?: number;   // Mano de obra (editable)
  partsCost?: number;   // Repuestos (editable)
  deductible?: number;  // Deducible (editable)
  totalCost?: number;   // Monto aprobado por comisión (editable)
  parts?: string[];
  thirdPartyInfo?: ClaimThirdPartyInfo;
  documents?: any[];
}

export interface MinistryDecision {
  status?: string | null;
  channel?: string | null;
  decidedAt?: string | null;
  actorLabel?: string | null;
  actorMail?: string | null;
  actorRole?: string | null;
  fromStatusSlug?: string | null;
  toStatusSlug?: string | null;
  tokenPending?: boolean;
  tokenExpiresAt?: string | null;
  hasToken?: boolean;
}

export interface CommissionDecision {
  status?: string | null;
  channel?: string | null;
  decidedAt?: string | null;
  actorLabel?: string | null;
  actorMail?: string | null;
  actorRole?: string | null;
  fromStatusSlug?: string | null;
  toStatusSlug?: string | null;
  documentUuid?: string | null;
  gloss?: string | null;
  documentTypeName?: string | null;
  requiresDocument?: boolean;
}

export interface PaymentDecision {
  status?: string | null;
  channel?: string | null;
  decidedAt?: string | null;
  actorLabel?: string | null;
  actorMail?: string | null;
  actorRole?: string | null;
  fromStatusSlug?: string | null;
  toStatusSlug?: string | null;
}

export interface CommissionVotoMatch {
  found: boolean;
  voto?: string;
  requestUuid?: string;
  document?: {
    id: number;
    uuid: string;
    fileName: string;
    mimeType?: string | null;
  };
}

export type BenefitType =
  | 'alarm'
  | 'sensor'
  | 'gps'
  | 'security_film'
  | 'roof_rack'
  | 'hands_free'
  | 'glass_engraving'
  | 'license'
  | 'notary'
  | 'rental_car'
  | 'tow_truck'
  | 'custody';

export interface Benefit {
  id: number;
  type: BenefitType;
  amount: number;
  date: string;
  status: 'requested' | 'approved' | 'rejected' | 'paid';
  vehicleId?: number;
  vehicle?: any;
  capAmount?: number | null;
  overCap?: boolean;
  capRuleSlug?: string | null;
  capPercent?: number | null;
}

export interface Claim {
  uuid: string;
  requestId?: string;
  statusSlug?: string;
  claimType?: string;
  laborCost?: number;
  partsCost?: number;
  deductible?: number;
  totalCost?: number;
  details?: ClaimDetails[];
  documents?: any[];
  thirdPartyInfo?: ClaimThirdPartyInfo;
  vehicle?: any;
  thirdPartyBrand?: any;
  thirdPartyBrandName?: string;
  thirdPartyModel?: any;
  thirdPartyModelName?: string;
  thirdPartyYear?: number;
  ministroApprovedBy?: string;
  ministroApprovalDate?: string;
  approvedDeductible?: number;
  approvedAmount?: number;
  invoicedAmount?: number;
  invoicedAt?: string;
  invoicedBy?: string;
}

export interface Request {
  uuid: string;
  processSlug: string;
  process?: { name?: string; icon?: string };
  referenceUuid?: string;
  partyUuid?: string;
  statusSlug: string;
  /** Sesión de comisión abierta y enlazada que impide aprobar este paso en la bandeja. */
  sessionHold?: { uuid: string } | null;
  statusObj?: {
    name: string;
    style: string;
  };
  /** Snapshot del status-flow BPM congelado al crear / aplicar a abiertas. */
  flowSnapshot?: {
    capturedAt?: string;
    processSlug?: string;
    steps?: Array<{
      stepSequence?: number;
      statusSlug?: string;
      statusName?: string;
      requiredRole?: string | null;
      requiresDocument?: boolean;
      documentTypeSlug?: string | null;
      [key: string]: any;
    }>;
  } | null;
  /** Snapshot de requisitos de documentos congelado al crear / sync abiertas. */
  documentRequirementsSnapshot?: {
    capturedAt?: string;
    processSlug?: string;
    items?: Array<{
      processSlug?: string;
      ownerSlug?: string;
      categorySlug?: string;
      typeSlug?: string;
      isRequired?: boolean;
      maxDaysAfterProcess?: number;
      [key: string]: any;
    }>;
  } | null;
  entityCode?: string | number;
  targetEntityCode?: string | number;
  year?: number | null;
  period?: number | null;
  entity?: { code?: number; name?: string; campus?: string };
  createdAt: string;
  updatedAt?: string;
  title: string;
  reference: string;
  party?: any;
  vehicle?: any;
  claim?: Claim;
  benefit?: Benefit | null;
  reimbursements?: any[];
  reason?: string;
  rejectionReason?: string | null;
  confirmationToken?: string;
  tokenExpiresAt?: string;
  confirmedAt?: string;
  ministryDecision?: MinistryDecision;
  commissionDecision?: CommissionDecision | null;
  paymentDecision?: PaymentDecision | null;
  /** Ministro aprobó y un archivo del detalle sigue observado o pendiente de corrección. */
  needsOriginInbox?: boolean;
  createdByEmail?: string | null;
  createdByRole?: string | null;
  flowHistory?: Array<{
    fromStatusSlug?: string;
    toStatusSlug?: string;
    status?: string;
    channel?: string | null;
    actorLabel?: string | null;
    actorMail?: string | null;
    actorRole?: string | null;
    actingAsRole?: string | null;
    decidedAt?: string | null;
    comments?: string | null;
    documentUuid?: string | null;
    gloss?: string | null;
    documentTypeName?: string | null;
    rejectionReason?: string | null;
    reason?: string | null;
  }>;
  transfer?: {
    originEntityCode: string | number;
    targetEntityCode: string | number;
  };
  details?: any;
  /** Monto propuesto (pago entidad) hasta crear entity_payment al aprobar. */
  amount?: number | null;
    payment?: {
      uuid: string;
      amount: number;
      paymentDate: string;
      year: number;
      period: number;
      document?: {
        uuid: string;
      };
    };
}

@Injectable({ providedIn: 'root' })
export class RequestService {
  private http = inject(HttpClient);
  private authService = inject(AuthService);
  
  private apiUrl = Global.url + 'V1';
  private docEndpoint = `${this.apiUrl}/documents`;

  // Estado Global Reactivo (la campana del header y la bandeja leen de aquí)
  requests = signal<Request[]>([]);
  isLoading = signal(false);
  historyRequests = signal<Request[]>([]);
  isHistoryLoading = signal(false);
  /** Deep-link / notificación: la bandeja selecciona este uuid cuando aparece en la lista. */
  focusRequestUuid = signal<string | null>(null);

  private lastEntityCode: any = null;
  private reloadTimer: any = null;
  private inFlightReload: Promise<Request[]> | null = null;

  constructor() {
    this.initEntityEffect();
  }

  // Escucha cambios de entidad para recargar solicitudes
  private initEntityEffect(): void {
    effect(() => {
      const entity = this.authService.sharingEntity();
      const code = entity?.['code'];
      if (code && code !== this.lastEntityCode) {
        this.lastEntityCode = code;
        this.loadRequests();
      }
    });
  }

  focusRequest(uuid: string | null | undefined): void {
    const id = String(uuid || '').trim();
    if (!id) return;
    this.focusRequestUuid.set(id);
  }

  clearFocusRequest(): void {
    this.focusRequestUuid.set(null);
  }

  // Solicita recarga de solicitudes con debounce
  public loadRequests(): void {
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
    this.reloadTimer = setTimeout(() => {
      void this.reloadRequests();
    }, 100);
  }

  // Recarga solicitudes reutilizando peticion en vuelo
  public reloadRequests(): Promise<Request[]> {
    if (this.inFlightReload) return this.inFlightReload;
    this.isLoading.set(true);
    this.inFlightReload = new Promise((resolve) => this._fetchReload(resolve));
    return this.inFlightReload;
  }

  // Finaliza recarga actualizando estado
  private _finishReload(data: Request[], resolve: (d: Request[]) => void): void {
    this.requests.set(data);
    this.isLoading.set(false);
    this.inFlightReload = null;
    resolve(data);
  }

  // Ejecuta peticion de solicitudes
  private _fetchReload(resolve: (d: Request[]) => void): void {
    this.getRequests().subscribe({
      next: (res) => {
        const list = Array.isArray(res) ? res : (res as any)?.data ?? [];
        this._finishReload(list, resolve);
      },
      error: () => this._finishReload([], resolve)
    });
  }

  getRequests(params?): Observable<Request[]> {
    const queryParams = QueryUtils.convertToFilter(params);
    return this.http.get<Request[]>(`${this.apiUrl}/requests`, { params: queryParams });
  }

  getHistoryRequests(params?: any): Observable<Request[]> {
    const queryParams = QueryUtils.convertToFilter(params);
    return this.http.get<Request[]>(`${this.apiUrl}/requests/history`, { params: queryParams });
  }

  public loadHistoryRequests(params?: any): Promise<Request[]> {
    this.isHistoryLoading.set(true);
    return new Promise((resolve) => {
      this.getHistoryRequests(params).subscribe({
        next: (res) => {
          const data = Array.isArray(res) ? res : (res as any)?.data ?? [];
          this.historyRequests.set(data);
          this.isHistoryLoading.set(false);
          resolve(data);
        },
        error: () => {
          this.isHistoryLoading.set(false);
          resolve([]);
        }
      });
    });
  }

  getRequestByUuid(uuid: string): Observable<Request> {
    return this.http.get<Request>(`${this.apiUrl}/requests/uuid/${uuid}`).pipe(
      // Algunos wrappers envuelven en { data }; la bandeja necesita el request plano.
      map((res: any) => (res?.uuid ? res : res?.data) as Request)
    );
  }

  /** Soft-discard: DRAFT → DISCARDED (recuperable). */
  discardRequest(uuid: string): Observable<Request> {
    return this.http.post<Request>(`${this.apiUrl}/requests/${uuid}/discard`, {});
  }

  /** Reactiva DISCARDED → paso inicial del flowSnapshot. */
  reactivateRequest(uuid: string): Observable<Request> {
    return this.http.post<Request>(`${this.apiUrl}/requests/${uuid}/reactivate`, {});
  }

  /** Hard-delete en cascada (solo soporte/ADM). */
  deleteRequest(uuid: string): Observable<any> {
    return this.http.delete(`${this.apiUrl}/requests/${uuid}`);
  }

  updateDocument(params: any): Observable<any> {
    const origin = (params.origin || 'PARTY').toUpperCase();
    let endpoint = `${this.apiUrl}/document-party`;

    if (origin === 'VEHICLE') endpoint = `${this.apiUrl}/document-vehicle`;
    if (origin === 'CLAIM') endpoint = `${this.apiUrl}/document-claim`;
    if (origin === 'PAYMENT') endpoint = `${this.apiUrl}/payments`;

    return this.http.put(`${endpoint}/${params.id}`, params);
  }

  getDocumentsByEntity(params: any): Observable<any> {
    const httpParams = QueryUtils.convertToFilter(params);
    return this.http.get(`${this.apiUrl}/documents-view`, { params: httpParams });
  }

  /**
   * Registra avance / rechazo / rewind BPM.
   * POST /requests/:uuid/flow-steps
   */
  advanceFlowStep(uuid: string, body: {
    toStatusSlug: string;
    gloss?: string | null;
    voto?: string | null;
    documentUuid?: string | null;
    documentTypeName?: string | null;
    requiresDocument?: boolean | null;
    rejectionReason?: string | null;
    reason?: string | null;
    comments?: string | null;
    channel?: string | null;
    paymentDate?: string | null;
    approvedDeductible?: number | null;
    approvedAmount?: number | null;
    invoicedAmount?: number | null;
  }): Observable<any> {
    return this.http.post(`${this.apiUrl}/requests/${uuid}/flow-steps`, body);
  }

  /**
   * Compat: si trae statusSlug/toStatusSlug → advanceFlowStep;
   * si no, PUT de datos de la solicitud.
   */
  updateRequestStatus(params: any): Observable<any> {
    const uuid = params?.uuid;
    const toStatusSlug = params?.toStatusSlug ?? params?.statusSlug;
    if (uuid && toStatusSlug) {
      return this.advanceFlowStep(uuid, {
        toStatusSlug,
        gloss: params.gloss ?? params.voto ?? params.remarks ?? null,
        voto: params.voto ?? null,
        documentUuid: params.documentUuid ?? null,
        documentTypeName: params.documentTypeName ?? null,
        requiresDocument: params.requiresDocument ?? null,
        rejectionReason: params.rejectionReason ?? params.reason ?? null,
        reason: params.reason ?? null,
        comments: params.comments ?? null
      });
    }
    return this.http.put(`${this.apiUrl}/requests/${uuid}`, params);
  }

  resendToken(idOrUuid: string | number): Observable<any> {
    return this.http.post(`${this.apiUrl}/requests/${idOrUuid}/resend-token`, {});
  }

  updateClaim(claimUuid: string, params: any): Observable<any> {
    return this.http.put(`${this.apiUrl}/claims/${claimUuid}`, params);
  }

  uploadClaimDocument(claimUuid: string, file: File, typeSlug: string): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('claimUuid', claimUuid);
    formData.append('typeSlug', typeSlug);
    return this.http.post(`${this.apiUrl}/claims/${claimUuid}/documents`, formData);
  }

  reuseClaimDocument(claimUuid: string, documentUuid: string, typeSlug = 'VOTO_COMISION'): Observable<any> {
    return this.http.post(`${this.apiUrl}/claims/${claimUuid}/documents/reuse`, { documentUuid, typeSlug });
  }

  findCommissionVoto(glosa: string, entityCode?: string | number | null): Observable<CommissionVotoMatch> {
    const encoded = encodeURIComponent(glosa.trim());
    const params: Record<string, string> = {};
    if (entityCode != null && entityCode !== '') params['entityCode'] = String(entityCode);
    return this.http.get<CommissionVotoMatch>(`${this.apiUrl}/requests/votos/${encoded}`, { params });
  }

  uploadPaymentDocument(uuid: string, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post(`${this.apiUrl}/payments/${uuid}/document`, formData);
  }
}
 
 
